import cookie from 'js-cookie';
import injectClickOutside from 'react-click-outside';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { noop } from 'lodash';
import { grey25 } from '@zocdoc/branding/colors';
import { FontFamily } from '../../../typography';
import UnstyledArrow from '../../../icons/Arrow';
import { DesktopMyAccountLinks, DropdownLinks, MyAccountLinksConversionIds } from '../constants';
import { FormattedMessage } from 'react-intl';
import { Link } from '../../../link';
import { Metrics, MetricsClickable } from '@zocdoc/frontend-metrics';

class SignInDropdownView extends React.PureComponent {
    static propTypes = {
        defaultOpen: PropTypes.bool,
        displayName: PropTypes.string,
        onSignInDropdownClick: PropTypes.func,
        onSignInDropdownLinkClick: PropTypes.func,
        trackSignInLinkClickConversion: PropTypes.func,
    };
    static defaultProps = {
        defaultOpen: false,
        onSignInDropdownClick: noop,
        onSignInDropdownLinkClick: noop,
        trackSignInLinkClickConversion: noop,
    };
    state = {
        dropdownOpen: this.props.defaultOpen,
    };

    render() {
        const { displayName } = this.props;
        const { dropdownOpen } = this.state;

        const arrowDirection = dropdownOpen ? 'up' : 'down';

        return (
            <Metrics component="SignInDropdown">
                <DropdownContainer>
                    <MetricsClickable
                        action="Click Sign in Dropdown"
                        element="ClosedDropdown"
                    >
                        <DropdownMenuBar
                            data-test="signin-dropdown-bar"
                            onClick={this.handleClickDropdownMenuBar}
                        >
                            <DropdownMenuHeader>
                                {displayName}
                            </DropdownMenuHeader>
                            <StyledArrow
                                direction={arrowDirection}
                                height={8}
                            />
                        </DropdownMenuBar>
                    </MetricsClickable>
                    {this.maybeRenderDropdownMenu()}
                </DropdownContainer>
            </Metrics>
        );
    }

    maybeRenderDropdownMenu() {
        if (this.state.dropdownOpen) {
            return (
                <DropdownMenuContainer>
                    {DesktopMyAccountLinks.map(link => {
                        return (
                            <DropdownMenuItem
                                key={link.dataTest}
                                data-test={`signin-dropdown-${link.dataTest}`}
                            >
                                <DropdownKey>
                                    <FormattedMessage {...link.key} />
                                </DropdownKey>

                                <DropdownLinkContainer >
                                    <MetricsClickable
                                        action={link.metrics.action}
                                        element={link.metrics.element}
                                    >
                                        <SignInLink
                                            data-test={`signin-dropdown-link-${link.dataTest}`}
                                            href={link.url}
                                            onClick={this.handleClickSigninLink}
                                        >
                                            <FormattedMessage {...link.message} />
                                        </SignInLink>
                                    </MetricsClickable>
                                </DropdownLinkContainer>
                            </DropdownMenuItem>
                        );
                    })}
                </DropdownMenuContainer>
            );
        }
    }

    getConversionIdForLink = url => {
        if (url === DropdownLinks.providerSignin) {
            return MyAccountLinksConversionIds.provider;
        } else if (url === DropdownLinks.patientSignin) {
            return MyAccountLinksConversionIds.patient;
        }
        return null;
    };

    handleClickSigninLink = event => {
        const {
            onSignInDropdownLinkClick,
            trackSignInLinkClickConversion,
        } = this.props;
        const { location } = window;

        onSignInDropdownLinkClick(event);
        trackSignInLinkClickConversion({
            conversionId: this.getConversionIdForLink(event.currentTarget.getAttribute('href')),
            sessionId: cookie.get('bsid').replace(/_.*$/, '').replace('-', ''),
            url: `${location.protocol}//${location.host}${location.pathname}`,
            userAgent: navigator.userAgent,
        });
    };

    handleClickOutside = () => {
        this.setState({ dropdownOpen: false });
    };

    handleClickDropdownMenuBar = event => {
        const { onSignInDropdownClick } = this.props;
        const dropdownToggled = !this.state.dropdownOpen;

        if (dropdownToggled && onSignInDropdownClick) {
            onSignInDropdownClick(event);
        }

        this.setState({ dropdownOpen: dropdownToggled });
    };
}

const DropdownContainer = styled.div`
    width: 100%;
    position: relative;
`;
const DropdownMenuBar = styled.div`
    display: flex;
    width: 100%;
    border-style: solid;
    cursor: pointer;
    align-items: center;
`;
const DropdownMenuHeader = styled(Link)`
    display: inline-block;
    font-size: 14px;
    color: ${p => p.theme.header.usernameDropdown.itemTextColor};
`;
const StyledArrow = styled(UnstyledArrow)`
    flex-grow: 0;
    flex-shrink: 0;
    margin-left: 4px;
    margin-right: 0px;
    pointer-events: none;
    transform: translateY(-0.5px);
    padding-top: 2px;
`;
const DropdownMenuContainer = styled.div`
    background-color: ${p => p.theme.header.usernameDropdown.itemBgColor};
    border: 1px solid ${grey25};
    min-width: 220px;
    margin-top: 12px;
    position: absolute;
    right: 0;
    z-index: 20;
`;
const DropdownMenuItem = styled.div`
    align-items: center;
    background-color: ${p => p.theme.header.usernameDropdown.itemBgColor};
    color: ${p => p.theme.header.usernameDropdown.itemTextColor};
    display: flex;
    height: 40px;
    margin: 0 16px 13px;
    padding-top: 13px;

    :not(:first-child) {
        border-top: 2px solid ${grey25};
    }

    *:nth-child(1) {
        flex: 1 0 40%;
    }

    *:nth-child(2) {
        flex: 1 0 60%;
    }
`;

const DropdownKey = styled.div`
    display: inline-block;
    font: 14px/24px ${FontFamily.SharpSansBold};
`;
const DropdownLinkContainer = styled.div`
    display: inline-block;
    font: 14px/29px ${FontFamily.SharpSansSemibold};
`;
const SignInLink = styled(Link)`
    color: ${p => p.theme.header.usernameDropdown.itemTextColor};
`;
export const SignInDropdown = withTheme(injectClickOutside(SignInDropdownView));
